import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { GitMerge, GitPullRequest, GitPullRequestClosed } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { diffKleur } from "@/lib/diff-kleur";
import { wanneer } from "./RepoVastleggingen";

/**
 * EG6 (CA4, opdracht Frank 1 november 2026): pull requests van tak naar doeltak, met de diff per bestand.
 *
 * Indeling naar GitHub: een lijst met Open en Gesloten, "Nieuwe pull request" met van en naar, en een pull request met de
 * kop (#nummer, titel, status, van naar naar) en de tabbladen Gesprek en Bestanden (per bestand de diff met plus en min).
 * API-vorm naar Gitea (`pull.go`). Samenvoegen hoort bij EG7 en staat in `PullSamenvoegen`.
 */
const ICOON = { open: GitPullRequest, samengevoegd: GitMerge, gesloten: GitPullRequestClosed };
const KLEUR = { open: "text-emerald-600", samengevoegd: "text-violet-600", gesloten: "text-red-600" };

export default function RepoPulls({ repo, takken, onGewijzigd = () => {}, Extra = null, startNieuw = false, standaardVan = "",
                                    onTakWeg = () => {} }) {
  const { t, taal } = useTaal();
  const hoofd = repo.default_branch || "main";
  const [lijst, setLijst] = useState([]);
  const [filter, setFilter] = useState("open");
  // GL225-4: "Pull request maken" in Code opent dit formulier meteen, met de werktak als van.
  const [nieuw, setNieuw] = useState(startNieuw);
  const [gekozen, setGekozen] = useState(null);
  const [tab, setTab] = useState("gesprek");
  const anderen = takken.filter((b) => b !== hoofd);
  const [vorm, setVorm] = useState({ van: (anderen.includes(standaardVan) && standaardVan) || anderen[0] || "", naar: hoofd, titel: "", tekst: "" });

  const laad = useCallback(() => {
    axios.get(`${API}/repos/${repo.id}/pulls`).then((r) => setLijst(r.data)).catch(() => setLijst([]));
  }, [repo.id]);
  useEffect(() => { laad(); }, [laad]);

  const fout = (e, standaard) => {
    const tekst = e?.response?.data?.detail;
    toast.error(typeof tekst === "string" ? tekst : standaard);
  };

  async function open(nummer) {
    try {
      const { data } = await axios.get(`${API}/repos/${repo.id}/pulls/${nummer}`);
      setGekozen(data);
      setTab("gesprek");
    } catch (e) {
      fout(e, t("De pull request kon niet worden geopend."));
    }
  }

  async function maak(e) {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/repos/${repo.id}/pulls`, vorm);
      setNieuw(false);
      setVorm({ ...vorm, titel: "", tekst: "" });
      laad();
      open(data.nummer);
    } catch (err) {
      fout(err, t("De pull request kon niet worden gemaakt."));
    }
  }

  async function zet(status) {
    try {
      await axios.post(`${API}/repos/${repo.id}/pulls/${gekozen.nummer}/status`, { status });
      laad();
      open(gekozen.nummer);
    } catch (err) {
      fout(err, t("Dat lukte niet."));
    }
  }

  if (gekozen) {
    const Icoon = ICOON[gekozen.status] || GitPullRequest;
    return (
      <div className="space-y-2" data-testid="pull-detail">
        <button type="button" className="text-sm text-primary hover:underline" onClick={() => { setGekozen(null); laad(); }}>
          {t("Terug naar de pull requests")}
        </button>
        <div className="rounded-lg border p-3">
          <p className="break-words text-sm font-semibold">{gekozen.titel} <span className="text-muted-foreground">#{gekozen.nummer}</span></p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className={`flex items-center gap-1 font-medium ${KLEUR[gekozen.status]}`} data-testid="pull-status">
              <Icoon className="h-3.5 w-3.5" />{t(gekozen.status)}
            </span>
            <span className="text-muted-foreground"><code>{gekozen.van}</code> {t("naar")} <code>{gekozen.naar}</code></span>
          </p>
          {gekozen.tak_weg && <p className="mt-1 text-xs text-muted-foreground" data-testid="pull-tak-weg">{t("Een van de twee takken bestaat niet meer.")}</p>}
        </div>
        <div className="flex gap-1 border-b text-sm" role="tablist">
          {[["gesprek", t("Gesprek")], ["bestanden", t("Bestanden")]].map(([id, naam]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`pull-tab-${id}`}
                    className={`tap-target px-3 py-1.5 ${tab === id ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}>
              {naam}{id === "bestanden" ? ` (${gekozen.bestanden.length})` : ""}
            </button>
          ))}
        </div>
        {tab === "gesprek" && (
          <div className="space-y-2" data-testid="pull-gesprek">
            <p className="whitespace-pre-wrap break-words rounded-lg border p-3 text-sm">{gekozen.tekst || t("Geen beschrijving.")}</p>
            {gekozen.commits.length > 0 && (
              <ol className="divide-y rounded-lg border text-sm" data-testid="pull-commits">
                {gekozen.commits.map((c) => (
                  <li key={c.sha} className="flex gap-2 px-3 py-1.5"><span className="min-w-0 flex-1 break-words">{c.boodschap}</span><code className="text-xs text-muted-foreground">{c.kort}</code></li>
                ))}
              </ol>
            )}
            {Extra && <Extra repo={repo} pull={gekozen} onVernieuwd={(d) => { setGekozen(d); laad(); onGewijzigd(); }}
                              onTakWeg={onTakWeg} />}
            {gekozen.status !== "samengevoegd" && (
              <Button type="button" size="sm" variant="outline" className="tap-target" data-testid="pull-sluit"
                      onClick={() => zet(gekozen.status === "open" ? "gesloten" : "open")}>
                {gekozen.status === "open" ? t("Sluit de pull request") : t("Heropen de pull request")}
              </Button>
            )}
          </div>
        )}
        {tab === "bestanden" && (
          <div className="space-y-2" data-testid="pull-bestanden">
            {gekozen.bestanden.map((b) => (
              <div key={b.pad} className="rounded-lg border" data-testid={`pull-bestand-${b.pad}`}>
                <div className="flex items-center gap-2 border-b px-3 py-1.5 text-xs">
                  <span className="min-w-0 flex-1 break-all">{b.pad}</span>
                  <span className="text-emerald-600">+{b.bij}</span><span className="text-red-600">-{b.af}</span>
                </div>
                <pre className="overflow-x-auto text-xs leading-5"><code>{b.diff.split("\n").map((r, i) => (
                  <div key={i} className={`whitespace-pre px-3 ${diffKleur(r)}`}>{r || " "}</div>
                ))}</code></pre>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const zichtbaar = lijst.filter((p) => (filter === "open" ? p.status === "open" : p.status !== "open"));
  return (
    <div className="space-y-2" data-testid="repo-pulls">
      <div className="flex flex-wrap items-center gap-2">
        {[["open", t("Open")], ["dicht", t("Gesloten")]].map(([id, naam]) => (
          <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`pulls-${id}`}
                  className={`tap-target rounded-md px-2 py-1 text-sm ${filter === id ? "bg-muted font-semibold" : "text-muted-foreground"}`}>
            {naam} ({lijst.filter((p) => (id === "open" ? p.status === "open" : p.status !== "open")).length})
          </button>
        ))}
        <Button type="button" size="sm" className="tap-target ml-auto" onClick={() => setNieuw(!nieuw)} disabled={!anderen.length}
                data-testid="pull-nieuw">{t("Nieuwe pull request")}</Button>
      </div>
      {!anderen.length && <p className="text-xs text-muted-foreground">{t("Maak eerst een tak naast {hoofd}.", { hoofd })}</p>}
      {nieuw && (
        <form onSubmit={maak} className="space-y-2 rounded-lg border p-3" data-testid="pull-vorm">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <select value={vorm.naar} onChange={(e) => setVorm({ ...vorm, naar: e.target.value })} aria-label={t("Naar")}
                    className="rounded-md border bg-transparent px-1 py-1" data-testid="pull-naar">
              {takken.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <span className="text-muted-foreground">{"<-"}</span>
            <select value={vorm.van} onChange={(e) => setVorm({ ...vorm, van: e.target.value })} aria-label={t("Van")}
                    className="rounded-md border bg-transparent px-1 py-1" data-testid="pull-van">
              {takken.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <input value={vorm.titel} onChange={(e) => setVorm({ ...vorm, titel: e.target.value })} placeholder={t("Titel")}
                 aria-label={t("Titel")} className="w-full rounded-md border bg-transparent px-2 py-1 text-sm" data-testid="pull-titel" />
          <textarea value={vorm.tekst} onChange={(e) => setVorm({ ...vorm, tekst: e.target.value })} placeholder={t("Beschrijving")}
                    aria-label={t("Beschrijving")} className="min-h-[80px] w-full rounded-md border bg-transparent px-2 py-1 text-sm"
                    data-testid="pull-tekst" />
          <Button type="submit" size="sm" className="tap-target" disabled={!vorm.titel.trim()} data-testid="pull-maak">
            {t("Pull request maken")}
          </Button>
        </form>
      )}
      <ul className="divide-y rounded-lg border" data-testid="pulls-lijst">
        {zichtbaar.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">{t("Geen pull requests.")}</li>}
        {zichtbaar.map((p) => {
          const Icoon = ICOON[p.status] || GitPullRequest;
          return (
            <li key={p.nummer}>
              <button type="button" onClick={() => open(p.nummer)} data-testid={`pull-${p.nummer}`}
                      className="tap-target flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted/50">
                <Icoon className={`mt-0.5 h-4 w-4 shrink-0 ${KLEUR[p.status]}`} />
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-sm">{p.titel}</span>
                  <span className="block text-xs text-muted-foreground">#{p.nummer} · <code>{p.van}</code> {t("naar")} <code>{p.naar}</code> · {wanneer(new Date(p.gemaakt_op * 1000).toISOString(), taal)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
